import type { ClimateRecipeWeek, RoomClimateTargets } from './ClimateTargets';
import type { FloweringWeekSettings } from './Batch';

export type BatchScheduleMode =
  | 'off'
  | 'preview'
  | 'active'
  | 'hold_week'
  | 'manual_override'
  | 'emergency_stop';

export type ActiveTargetStage = 'pregrow_production' | 'flowering';

export interface StagePhotoperiod {
  lightsOnTime: { hour: number; minute: number };
  lightHours: number;
}

export interface BatchPhotoperiod {
  pregrow: StagePhotoperiod;
  flowering: StagePhotoperiod;
}

export const DEFAULT_PHOTOPERIOD: BatchPhotoperiod = {
  pregrow: { lightsOnTime: { hour: 6, minute: 0 }, lightHours: 18 },
  flowering: { lightsOnTime: { hour: 6, minute: 0 }, lightHours: 12 },
};

export function resolvePhotoperiodForStage(
  batch: { photoperiod?: BatchPhotoperiod } | null | undefined,
  stage: ActiveTargetStage,
): StagePhotoperiod {
  const key = stage === 'pregrow_production' ? 'pregrow' : 'flowering';
  const stored = batch?.photoperiod?.[key];
  const defaults = DEFAULT_PHOTOPERIOD[key];
  if (!stored) return { ...defaults, lightsOnTime: { ...defaults.lightsOnTime } };
  const lightsOnTime = stored.lightsOnTime
    ? { hour: stored.lightsOnTime.hour ?? defaults.lightsOnTime.hour, minute: stored.lightsOnTime.minute ?? defaults.lightsOnTime.minute }
    : { ...defaults.lightsOnTime };
  const lightHours = typeof stored.lightHours === 'number' ? stored.lightHours : defaults.lightHours;
  return { lightsOnTime, lightHours };
}

/**
 * True when a photoperiod means 24h continuous light (no dark period). Used to
 * avoid the "lights off = (on + 24) % 24 = on" collapse where start === end is
 * (mis)treated as a zero-length window by time-window logic. Some pre-grow /
 * supporting-grow regimes run lights 24/7.
 */
export function isContinuousLight(lightHours: number | null | undefined): boolean {
  return typeof lightHours === 'number' && lightHours >= 24;
}

export type ActiveRecipeKey = 'pregrowClimateRecipe' | 'climateRecipe' | 'floweringWeekSettings';

export interface DehuCascadeStageConfig {
  enabled: boolean;
  deviceId?: string;
  offset: number;
  minRuntimeSeconds?: number;
}

/** Per-phase max active dehu stages for v2 governor (crop-stage capacity caps). */
export interface DehuMaxActiveStagesByPhase {
  pregrow_production?: number;
  early_flower?: number;
  mid_flower?: number;
  late_flower?: number;
}

/** v2 target-centered dehumidification parameters (room automation config). */
export interface DehuV2AutomationConfig {
  dehuControlBandRh?: number;
  dehuStageAddOffsetsRh?: number[];
  dehuStageRemoveOffsetsRh?: number[];
  dehuStageOnDelaySeconds?: number[];
  dehuMinOnSeconds?: number;
  dehuMinOffSeconds?: number;
  dehuEmergencyMarginRh?: number;
  dehuMaxActiveStagesByPhase?: DehuMaxActiveStagesByPhase;
  /** Explicit migration flag — reconcile/regenerate writes v2 rules only when set. */
  dehuControlMigration?: 'v2';
}

export interface BatchScheduleAutomationConfig extends DehuV2AutomationConfig {
  humiditySensorId?: string;
  dehuDeadband?: number;
  dehuMinDeadband?: number;
  dehuStages?: DehuCascadeStageConfig[];
  co2SensorId?: string;
  co2EnrichmentDeviceId?: string;
  co2ExhaustDeviceId?: string;
  co2Deadband?: number;
  lightDeviceIds?: string[];
  lightsOnTime?: { hour: number; minute: number };
}

export function normalizeDehuStageOffset(offset: number | undefined): number {
  return Math.abs(Number.isFinite(offset) ? offset as number : 0);
}

export function resolveDehuDeadband(configuredDeadband: number | undefined, minDeadband = 3): number {
  const configured = Number.isFinite(configuredDeadband) ? configuredDeadband as number : 5;
  const minimum = Number.isFinite(minDeadband) ? minDeadband : 3;
  return Math.max(configured, minimum);
}

/** @deprecated Legacy ON formula (target + deadband + offset). Use computeDehuStageOnThreshold. */
export function computeDehuStageSetpoint(target: number, deadband: number | undefined, offset: number | undefined, minDeadband = 3): number {
  return target + resolveDehuDeadband(deadband, minDeadband) + normalizeDehuStageOffset(offset);
}

/** Per-stage dehu ON threshold: recipe max RH target + stage capacity offset. */
export function computeDehuStageOnThreshold(targetMax: number, offset: number | undefined): number {
  return targetMax + normalizeDehuStageOffset(offset);
}

/** Shared dehu OFF / release threshold below the recipe max RH target. */
export function computeDehuOffThreshold(targetMax: number, deadband: number | undefined, minDeadband = 3): number {
  return targetMax - resolveDehuDeadband(deadband, minDeadband);
}

// ---------------------------------------------------------------------------
// v2 target-centered staged dehumidification (recipe optimal = RH setpoint)
// ---------------------------------------------------------------------------

export type DehuControlVersion = 1 | 2;

/** Conservative v2 defaults (editable per room). */
export const DEFAULT_DEHU_V2_CONFIG: Required<Pick<
  DehuV2AutomationConfig,
  | 'dehuControlBandRh'
  | 'dehuStageAddOffsetsRh'
  | 'dehuStageRemoveOffsetsRh'
  | 'dehuStageOnDelaySeconds'
  | 'dehuMinOnSeconds'
  | 'dehuMinOffSeconds'
  | 'dehuEmergencyMarginRh'
  | 'dehuMaxActiveStagesByPhase'
>> = {
  dehuControlBandRh: 2,
  dehuStageAddOffsetsRh: [1, 2, 3],
  dehuStageRemoveOffsetsRh: [-1, -2, -3],
  dehuStageOnDelaySeconds: [0, 60, 120],
  dehuMinOnSeconds: 300,
  dehuMinOffSeconds: 120,
  dehuEmergencyMarginRh: 5,
  dehuMaxActiveStagesByPhase: {
    pregrow_production: 1,
    early_flower: 1,
    mid_flower: 2,
    late_flower: 3,
  },
};

export function resolveDehuV2Config(config?: DehuV2AutomationConfig): typeof DEFAULT_DEHU_V2_CONFIG {
  const d = DEFAULT_DEHU_V2_CONFIG;
  return {
    dehuControlBandRh: config?.dehuControlBandRh ?? d.dehuControlBandRh,
    dehuStageAddOffsetsRh: config?.dehuStageAddOffsetsRh?.length
      ? [...config.dehuStageAddOffsetsRh]
      : [...d.dehuStageAddOffsetsRh],
    dehuStageRemoveOffsetsRh: config?.dehuStageRemoveOffsetsRh?.length
      ? [...config.dehuStageRemoveOffsetsRh]
      : [...d.dehuStageRemoveOffsetsRh],
    dehuStageOnDelaySeconds: config?.dehuStageOnDelaySeconds?.length
      ? [...config.dehuStageOnDelaySeconds]
      : [...d.dehuStageOnDelaySeconds],
    dehuMinOnSeconds: config?.dehuMinOnSeconds ?? d.dehuMinOnSeconds,
    dehuMinOffSeconds: config?.dehuMinOffSeconds ?? d.dehuMinOffSeconds,
    dehuEmergencyMarginRh: config?.dehuEmergencyMarginRh ?? d.dehuEmergencyMarginRh,
    dehuMaxActiveStagesByPhase: {
      ...d.dehuMaxActiveStagesByPhase,
      ...config?.dehuMaxActiveStagesByPhase,
    },
  };
}

export interface DehuV2StageThresholds {
  onRh: number;
  offRh: number;
  onDelaySeconds: number;
}

export interface DehuV2ComputedThresholds {
  setpoint: number;
  leadOnRh: number;
  leadOffRh: number;
  emergencyRh: number;
  stages: DehuV2StageThresholds[];
}

/** Compute per-stage ON/OFF RH thresholds around the recipe humidity setpoint. */
export function computeDehuV2Thresholds(
  setpoint: number,
  stageCount: number,
  config?: DehuV2AutomationConfig,
): DehuV2ComputedThresholds {
  const resolved = resolveDehuV2Config(config);
  const halfBand = resolved.dehuControlBandRh / 2;
  const stages: DehuV2StageThresholds[] = [];
  for (let i = 0; i < stageCount; i++) {
    const addOffset = resolved.dehuStageAddOffsetsRh[i]
      ?? resolved.dehuStageAddOffsetsRh[resolved.dehuStageAddOffsetsRh.length - 1];
    const removeOffset = resolved.dehuStageRemoveOffsetsRh[i]
      ?? resolved.dehuStageRemoveOffsetsRh[resolved.dehuStageRemoveOffsetsRh.length - 1];
    const onDelay = resolved.dehuStageOnDelaySeconds[i]
      ?? resolved.dehuStageOnDelaySeconds[resolved.dehuStageOnDelaySeconds.length - 1]
      ?? 0;
    stages.push({
      onRh: setpoint + addOffset,
      offRh: setpoint + removeOffset,
      onDelaySeconds: onDelay,
    });
  }
  if (stages.length > 0) {
    stages[0] = {
      onRh: setpoint + halfBand,
      offRh: setpoint - halfBand,
      onDelaySeconds: 0,
    };
  }
  return {
    setpoint,
    leadOnRh: setpoint + halfBand,
    leadOffRh: setpoint - halfBand,
    emergencyRh: setpoint + resolved.dehuEmergencyMarginRh,
    stages,
  };
}

/** Crop-stage capacity cap for v2 governor. */
export function resolveDehuMaxActiveStages(
  targetStage: ActiveTargetStage | undefined,
  targetWeek: number | undefined,
  config?: DehuV2AutomationConfig,
  lightState: 'day' | 'night' = 'day',
  enabledStageCount?: number,
): number {
  const caps = resolveDehuV2Config(config).dehuMaxActiveStagesByPhase;
  if (targetStage === 'pregrow_production') {
    const cap = caps.pregrow_production ?? 1;
    return clampDehuStageCap(cap, enabledStageCount);
  }
  const week = targetWeek ?? 1;
  let cap: number;
  if (week <= 3) {
    cap = lightState === 'night'
      ? Math.max(caps.mid_flower ?? 2, 2)
      : (caps.early_flower ?? 1);
  } else if (week <= 6) {
    cap = caps.mid_flower ?? 2;
  } else if (lightState === 'night') {
    cap = caps.late_flower ?? 3;
  } else {
    cap = caps.mid_flower ?? 2;
  }
  return clampDehuStageCap(cap, enabledStageCount);
}

/** Never exceed configured dehu hardware stages. */
export function clampDehuStageCap(cap: number, enabledStageCount?: number): number {
  if (typeof enabledStageCount === 'number' && enabledStageCount > 0) {
    return Math.min(cap, enabledStageCount);
  }
  return cap;
}

export function shouldUseDehuV2(
  roomData?: { dehuControlMigration?: string; automationConfig?: BatchScheduleAutomationConfig },
  options?: BatchScheduleCommandOptions,
): boolean {
  if (roomData?.dehuControlMigration === 'v2') return true;
  if (roomData?.automationConfig?.dehuControlMigration === 'v2') return true;
  if (options?.automationConfig?.dehuControlMigration === 'v2') return true;
  return false;
}

export interface BatchScheduleRoomState {
  batchScheduleMode?: BatchScheduleMode;
  /** Explicit v2 migration flag — reconcile will not auto-upgrade without this. */
  dehuControlMigration?: 'v2';
  activeTargetStage?: ActiveTargetStage;
  activeRecipeKey?: ActiveRecipeKey;
  currentTargetWeek?: number;
  schedulePinnedWeek?: number;
  activatedAt?: string;
  activatedBy?: string;
  deactivatedAt?: string;
  deactivatedBy?: string;
  lastScheduleAppliedAt?: string;
  lastScheduleAppliedBy?: string;
  lastScheduleValidationStatus?: 'valid' | 'warnings' | 'invalid';
  scheduleConflictStatus?: 'none' | 'warnings' | 'blocked';
  scheduleConflictIds?: string[];
  scheduleSnapshotId?: string;
  scheduleActivationId?: string;
  automationConfig?: BatchScheduleAutomationConfig;
}

export interface BatchScheduleCommandOptions {
  targetStage?: ActiveTargetStage;
  targetWeek?: number;
  weekOverride?: number;
  automationConfig?: BatchScheduleAutomationConfig;
  restoreSnapshot?: boolean;
  reason?: string;
  operatorId?: string;
  /**
   * Occupant batch id known to the caller BEFORE this activate overwrites
   * room.activeBatchId. When set and different from the batch being activated,
   * recipe-linked irrigation programs in the room are deleted (crop change).
   * Omit on same-crop Activate Schedule so a live grow is not wiped.
   */
  previousBatchId?: string;
}

export interface BatchScheduleResolution {
  targetStage: ActiveTargetStage;
  recipeKey: ActiveRecipeKey;
  currentTargetWeek: number;
  effectiveWeek: number;
  recipe: ClimateRecipeWeek;
  fallbackSource?: ActiveRecipeKey;
  /** True when applyHarvestDayClimateOverride replaced light/temp targets for harvest day. */
  harvestDayOverride?: boolean;
}

export function isPregrowStage(stage?: string | null): boolean {
  if (!stage) return false;
  // Same normalization as frontend isPreGrowInProduction — handles hyphens
  // (pre-grow_production) that .includes('pregrow') alone would miss.
  return stage.toLowerCase().replace(/[\s_-]+/g, '').includes('pregrow');
}

export function normalizeTargetStage(stage?: string | null, override?: ActiveTargetStage): ActiveTargetStage {
  if (override) return override;
  return isPregrowStage(stage) ? 'pregrow_production' : 'flowering';
}

export function parseFirestoreDate(raw: unknown): Date | null {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof (raw as { toDate?: () => Date }).toDate === 'function') {
    const d = (raw as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Elevia batch.stageDates.Flowering is SSOT; room.floweringStartDate can lag after stage flip. */
export function resolveFloweringStartDate(
  batchData?: { stageDates?: Record<string, unknown>; floweringStartDate?: unknown } | null,
  roomData?: { batchStageDates?: Record<string, unknown>; floweringStartDate?: unknown } | null,
): Date | null {
  const batchDates = (batchData?.stageDates ?? {}) as Record<string, unknown>;
  const roomDates = (roomData?.batchStageDates ?? {}) as Record<string, unknown>;
  return (
    parseFirestoreDate(batchDates.Flowering)
    || parseFirestoreDate(batchDates.flowering)
    || parseFirestoreDate(batchData?.floweringStartDate)
    || parseFirestoreDate(roomDates.Flowering)
    || parseFirestoreDate(roomDates.flowering)
    || parseFirestoreDate(roomData?.floweringStartDate)
    || null
  );
}

/** Match Elevia Lab: max(1, ceil(midnight-aligned elapsed days)). */
export function computeEleviaFloweringDay(start: Date | null, now = new Date()): number {
  if (!start) return 0;
  const midnight = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  return Math.max(1, Math.ceil((midnight(now).getTime() - midnight(start).getTime()) / 86400000));
}

export function computeFloweringWeekFromDay(dayOfFlower: number): number {
  if (dayOfFlower <= 0) return 1;
  return Math.max(1, Math.floor((dayOfFlower - 1) / 7) + 1);
}

/** Local calendar day (YYYY-MM-DD) of a Date. */
function localYmd(d: Date): string {
  return d.toLocaleDateString('en-CA');
}

/**
 * Calendar day part (YYYY-MM-DD) of a stage-date value. Elevia stageDates are
 * YYYY-MM-DD strings (taken verbatim); ISO strings keep their date part;
 * Timestamps/Dates resolve to the LOCAL calendar day (never toISOString —
 * a local-midnight Timestamp would key as the previous UTC day).
 */
export function stageDateYmd(raw: unknown): string | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
    if (m) return m[1];
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : localYmd(d);
  }
  const d = parseFirestoreDate(raw);
  return d ? localYmd(d) : null;
}

/**
 * Harvest plan-of-record. Elevia batch.stageDates.Harvest is SSOT;
 * batch.expectedHarvestDate and the room's batchStageDates mirror are
 * fallbacks. Returns YYYY-MM-DD or null when the batch carries no plan.
 *
 * Never derive harvest from floweringStart + an assumed length when this
 * resolves — the 63-day assumption cut Room 3's irrigation a day early
 * (2026-08-09) because its batch ran a planned 64-day flowering.
 *
 * A candidate that is NOT after the flowering start is an inverted leftover
 * plan (old Harvest date left on a batch that then got a new Flowering date —
 * Room 3, 2026-08-24: Harvest 11 Aug vs Flowering 19 Aug drove
 * daysUntilHarvest to 0 on a day-5 crop). Such candidates are skipped
 * per-candidate (a valid fallback behind an inverted stageDates.Harvest still
 * wins) and reported via onInvertedCandidate. When no flowering date resolves
 * at all, candidates are accepted unchecked — never reject a plan we cannot
 * verify. Same span rule as resolveHarvestAnnotation.
 */
export function resolveExpectedHarvestYmd(
  batchData?: {
    stageDates?: Record<string, unknown>;
    expectedHarvestDate?: unknown;
    floweringStartDate?: unknown;
  } | null,
  roomData?: { batchStageDates?: Record<string, unknown>; floweringStartDate?: unknown } | null,
  onInvertedCandidate?: (candidateYmd: string, floweringYmd: string) => void,
): string | null {
  const batchDates = (batchData?.stageDates ?? {}) as Record<string, unknown>;
  const roomDates = (roomData?.batchStageDates ?? {}) as Record<string, unknown>;
  const floweringYmd = resolveFloweringYmd(batchData, roomData);
  const candidates = [
    batchDates.Harvest,
    batchDates.harvest,
    batchData?.expectedHarvestDate,
    roomDates.Harvest,
    roomDates.harvest,
  ];
  for (const raw of candidates) {
    const ymd = stageDateYmd(raw);
    if (!ymd) continue;
    if (floweringYmd && ymdSpanDays(ymd, floweringYmd) <= 0) {
      onInvertedCandidate?.(ymd, floweringYmd);
      continue;
    }
    return ymd;
  }
  return null;
}

/**
 * Flowering-start calendar day (YYYY-MM-DD), same source ordering as
 * resolveFloweringStartDate but kept in YMD space so span comparisons against
 * stage-date strings are exact in every timezone (a YMD string parsed as a
 * UTC-midnight Date shifts a day west of UTC).
 */
function resolveFloweringYmd(
  batchData?: { stageDates?: Record<string, unknown>; floweringStartDate?: unknown } | null,
  roomData?: { batchStageDates?: Record<string, unknown>; floweringStartDate?: unknown } | null,
): string | null {
  const batchDates = (batchData?.stageDates ?? {}) as Record<string, unknown>;
  const roomDates = (roomData?.batchStageDates ?? {}) as Record<string, unknown>;
  return (
    stageDateYmd(batchDates.Flowering)
    ?? stageDateYmd(batchDates.flowering)
    ?? stageDateYmd(batchData?.floweringStartDate)
    ?? stageDateYmd(roomDates.Flowering)
    ?? stageDateYmd(roomDates.flowering)
    ?? stageDateYmd(roomData?.floweringStartDate)
    ?? null
  );
}

/** Whole calendar days from one YMD to another (positive when ymd is later). */
function ymdSpanDays(ymd: string, fromYmd: string): number {
  const [fy, fm, fd] = fromYmd.split('-').map(Number);
  return daysUntilYmd(ymd, new Date(fy, fm - 1, fd));
}

/**
 * Whole calendar days from `from`'s local day to the YMD day (negative when
 * past). Both sides midnight-aligned in local time, so DST-safe and
 * consistent with computeEleviaFloweringDay.
 */
export function daysUntilYmd(ymd: string, from = new Date()): number {
  const [y, m, d] = ymd.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const base = new Date(from);
  base.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - base.getTime()) / 86400000);
}

export function buildRecipeFromLegacy(weekSettings: FloweringWeekSettings[]): ClimateRecipeWeek[] {
  return weekSettings.map((settings) => {
    const vpd = settings.vpd || calculateVpd(settings.temperature, settings.humidity);
    const nightTemp = settings.temperature - 3;
    const nightHumidity = Math.max(30, settings.humidity - 5);
    const nightVpd = calculateVpd(nightTemp, nightHumidity);

    return {
      week: settings.week,
      day: {
        temperature: range(settings.temperature, 2),
        humidity: range(settings.humidity, 5),
        vpd: range(vpd, 0.1, 2),
        co2: {
          min: Math.max(400, settings.co2 - 100),
          optimal: settings.co2,
          max: settings.co2 + 100,
        },
      },
      night: {
        temperature: range(nightTemp, 2),
        humidity: range(nightHumidity, 5),
        vpd: range(nightVpd, 0.1, 2),
        co2: { min: 400, optimal: 600, max: 800 },
      },
      lightPercent: settings.lightPercent || 100,
      ppfd: settings.ppfd,
      lightHours: settings.lightHours || 12,
    };
  });
}

export function resolveBatchScheduleRecipe(args: {
  batchStage?: string | null;
  targetStage?: ActiveTargetStage;
  targetWeek?: number;
  currentFloweringWeek?: number;
  schedulePinnedWeek?: number;
  pregrowClimateRecipe?: ClimateRecipeWeek[];
  climateRecipe?: ClimateRecipeWeek[];
  floweringWeekSettings?: FloweringWeekSettings[];
}): BatchScheduleResolution {
  const targetStage = normalizeTargetStage(args.batchStage, args.targetStage);
  const currentTargetWeek = Math.max(1, Math.floor(
    args.schedulePinnedWeek ||
    args.targetWeek ||
    args.currentFloweringWeek ||
    1,
  ));

  const floweringRecipe = args.climateRecipe?.length
    ? args.climateRecipe
    : buildRecipeFromLegacy(args.floweringWeekSettings || []);

  let recipeKey: ActiveRecipeKey = targetStage === 'pregrow_production'
    ? 'pregrowClimateRecipe'
    : (args.climateRecipe?.length ? 'climateRecipe' : 'floweringWeekSettings');
  let recipeList = targetStage === 'pregrow_production'
    ? (args.pregrowClimateRecipe || [])
    : floweringRecipe;
  let fallbackSource: ActiveRecipeKey | undefined;

  if (targetStage === 'pregrow_production' && recipeList.length === 0) {
    recipeList = floweringRecipe;
    fallbackSource = args.climateRecipe?.length ? 'climateRecipe' : 'floweringWeekSettings';
    recipeKey = 'pregrowClimateRecipe';
  }

  if (recipeList.length === 0) {
    throw new Error('No climate recipe or legacy week settings found');
  }

  const maxWeek = Math.max(...recipeList.map((week) => week.week));
  const effectiveWeek = Math.min(currentTargetWeek, maxWeek);
  const recipe = recipeList.find((week) => week.week === effectiveWeek);
  if (!recipe) {
    throw new Error(`No recipe found for week ${effectiveWeek}`);
  }

  return {
    targetStage,
    recipeKey,
    currentTargetWeek,
    effectiveWeek,
    recipe,
    fallbackSource,
  };
}

// ---------------------------------------------------------------------------
// Harvest-day climate override (facility toggle: settings/harvestDayClimate)
// ---------------------------------------------------------------------------

export const DEFAULT_HARVEST_DAY_LIGHT_PERCENT = 20;
export const DEFAULT_HARVEST_DAY_TEMPERATURE = 23;

export interface HarvestDayClimateConfig {
  enabled: boolean;
  lightPercent: number;
  temperature: number;
}

/**
 * Normalize the raw settings/harvestDayClimate doc. Missing doc or
 * enabled !== true means OFF; invalid numbers fall back to defaults and
 * valid ones are clamped to safe ranges (lights 0-100%, temp 5-40°C).
 */
export function resolveHarvestDayClimateConfig(raw: unknown): HarvestDayClimateConfig {
  const data = (raw ?? {}) as Record<string, unknown>;
  const lp = Number(data.lightPercent);
  const temp = Number(data.temperature);
  return {
    enabled: data.enabled === true,
    lightPercent: Number.isFinite(lp) ? Math.min(100, Math.max(0, lp)) : DEFAULT_HARVEST_DAY_LIGHT_PERCENT,
    temperature: Number.isFinite(temp) ? Math.min(40, Math.max(5, temp)) : DEFAULT_HARVEST_DAY_TEMPERATURE,
  };
}

/**
 * True when the batch is on its harvest day: stage is Harvest (kept active if
 * cutting runs past the planned day), or the planned stageDates.Harvest falls
 * on the local calendar day (so the override starts at midnight, before the
 * stage daemon flips the batch). Uses the batch stageDates with the room's
 * batchStageDates mirror as fallback — deliberately NOT expectedHarvestDate,
 * which is a prediction rather than the plan of record.
 *
 * The date path applies the same inversion guard as resolveHarvestAnnotation:
 * a stale Harvest date that is not after the flowering start must never dim
 * lights / drop temperature on a fresh crop. The stage === 'harvest' path is
 * operator-confirmed and stays unguarded.
 */
export function isHarvestDayForBatch(
  batchData?: {
    stage?: string | null;
    stageDates?: Record<string, unknown>;
    floweringStartDate?: unknown;
  } | null,
  roomData?: { batchStageDates?: Record<string, unknown>; floweringStartDate?: unknown } | null,
  now = new Date(),
): boolean {
  const stage = typeof batchData?.stage === 'string' ? batchData.stage.trim().toLowerCase() : '';
  if (stage === 'harvest') return true;
  const batchDates = (batchData?.stageDates ?? {}) as Record<string, unknown>;
  const roomDates = (roomData?.batchStageDates ?? {}) as Record<string, unknown>;
  const harvestYmd = stageDateYmd(batchDates.Harvest)
    ?? stageDateYmd(batchDates.harvest)
    ?? stageDateYmd(roomDates.Harvest)
    ?? stageDateYmd(roomDates.harvest);
  if (!harvestYmd || harvestYmd !== localYmd(now)) return false;
  const floweringYmd = resolveFloweringYmd(batchData, roomData);
  if (floweringYmd && ymdSpanDays(harvestYmd, floweringYmd) <= 0) return false;
  return true;
}

/** Recipe-table placement of the harvest-day climate step. */
export interface HarvestAnnotation {
  /** Flowering week (1-10) the harvest day falls in. */
  week: number;
  /** Plan-of-record harvest day, YYYY-MM-DD (same keying as isHarvestDayForBatch). */
  harvestYmd: string;
}

/**
 * Where the recipe table should show the harvest-day climate step, derived
 * strictly from the same plan-of-record the controller acts on
 * (stageDates.Harvest / stageDates.Flowering, batch first then the room's
 * batchStageDates mirror — never expectedHarvestDate). Returns null when the
 * annotation would disagree with what the controller applies:
 *   - either stage date missing, or Harvest not after Flowering (stale mirror /
 *     inconsistent plan — the old UI math collapsed this to W1);
 *   - the harvest day already passed and the batch is not in Harvest stage
 *     (isHarvestDayForBatch can never fire again, so advertising a dimming
 *     step would be a lie). Stage Harvest keeps the annotation alive while a
 *     run-over cut is still in progress, mirroring the override behavior.
 */
export function resolveHarvestAnnotation(
  batchData?: { stage?: string | null; stageDates?: Record<string, unknown> } | null,
  roomData?: { batchStageDates?: Record<string, unknown> } | null,
  now = new Date(),
): HarvestAnnotation | null {
  const batchDates = (batchData?.stageDates ?? {}) as Record<string, unknown>;
  const roomDates = (roomData?.batchStageDates ?? {}) as Record<string, unknown>;
  const harvestYmd = stageDateYmd(batchDates.Harvest)
    ?? stageDateYmd(batchDates.harvest)
    ?? stageDateYmd(roomDates.Harvest)
    ?? stageDateYmd(roomDates.harvest);
  const floweringYmd = stageDateYmd(batchDates.Flowering)
    ?? stageDateYmd(batchDates.flowering)
    ?? stageDateYmd(roomDates.Flowering)
    ?? stageDateYmd(roomDates.flowering);
  if (!harvestYmd || !floweringYmd) return null;
  const [fy, fm, fd] = floweringYmd.split('-').map(Number);
  // Signed day-of-flower of the harvest day (harvest N days after flowering
  // start = flowering day N). <= 0 means an inverted/stale plan.
  const span = daysUntilYmd(harvestYmd, new Date(fy, fm - 1, fd));
  if (span <= 0) return null;
  const stage = typeof batchData?.stage === 'string' ? batchData.stage.trim().toLowerCase() : '';
  if (stage !== 'harvest' && daysUntilYmd(harvestYmd, now) < 0) return null;
  return { week: Math.min(10, Math.floor((span - 1) / 7) + 1), harvestYmd };
}

/**
 * Overlay harvest-day climate targets onto a resolved recipe: lights dimmed to
 * config.lightPercent and day+night temperature re-centered on
 * config.temperature (±2°C band via the same range() used by recipe builders,
 * so HVAC desiredTemp, the climate mirror, and alert thresholds stay
 * consistent). Humidity/CO2/VPD keep the flowering-week values. Returns the
 * resolution unchanged when the toggle is off or it is not harvest day.
 */
export function applyHarvestDayClimateOverride(
  resolution: BatchScheduleResolution,
  batchData: { stage?: string | null; stageDates?: Record<string, unknown> } | null | undefined,
  roomData: { batchStageDates?: Record<string, unknown> } | null | undefined,
  rawConfig: unknown,
  now = new Date(),
): BatchScheduleResolution {
  const config = resolveHarvestDayClimateConfig(rawConfig);
  if (!config.enabled) return resolution;
  if (!isHarvestDayForBatch(batchData, roomData, now)) return resolution;
  const recipe = resolution.recipe;
  return {
    ...resolution,
    harvestDayOverride: true,
    recipe: {
      ...recipe,
      lightPercent: config.lightPercent,
      day: { ...recipe.day, temperature: range(config.temperature, 2) },
      night: { ...recipe.night, temperature: range(config.temperature, 2) },
    },
  };
}

export function recipeToRoomClimate(recipe: ClimateRecipeWeek, lightState?: 'day' | 'night'): RoomClimateTargets {
  // Flat fields mirror the CURRENTLY-ACTIVE period (defaults to day if
  // lightState is unknown). day/night sub-objects always carry both so
  // downstream consumers can pick either explicitly.
  const active = lightState === 'night' ? recipe.night : recipe.day;
  return {
    day: recipe.day,
    night: recipe.night,
    lightPercent: recipe.lightPercent,
    lightHours: recipe.lightHours,
    temperature: active.temperature,
    humidity: active.humidity,
    vpd: active.vpd,
    co2: active.co2,
  };
}

export function buildRoomClimateUpdate(recipe: ClimateRecipeWeek, lightState?: 'day' | 'night'): Record<string, unknown> {
  return {
    climate: recipeToRoomClimate(recipe, lightState),
  };
}

export function defaultDehuStages(): DehuCascadeStageConfig[] {
  return [
    { enabled: true, offset: 0, minRuntimeSeconds: 120 },
    { enabled: true, offset: 3, minRuntimeSeconds: 120 },
    { enabled: true, offset: 5, minRuntimeSeconds: 120 },
  ];
}

function range(optimal: number, deadband: number, decimals = 1) {
  const factor = 10 ** decimals;
  return {
    min: Math.round((optimal - deadband) * factor) / factor,
    optimal,
    max: Math.round((optimal + deadband) * factor) / factor,
  };
}

function calculateVpd(tempC: number, rhPct: number): number {
  const svp = 0.6108 * Math.exp((17.27 * tempC) / (tempC + 237.3));
  return Math.round(svp * (1 - rhPct / 100) * 100) / 100;
}
